import { SelectMenu, Icon, TextField } from '@scale/scaleui';
import { PropsWithChildren, useCallback, useEffect } from 'react';
import {
  ResponseTextCollectionBasedConditionalVisibility,
  PromptTextCollectionBasedConditionalVisibility,
  TextCollectionAndOperator,
  TextCollectionBasedConditionalVisibility,
  TextCollectionFieldCondition,
  TextCollectionStepParams,
} from '@scale/shared/types/chat/params';

import { CategoryField } from '@scale/shared/types/params/textcollection';
import produce from 'immer';

const SUPPORTED_FIELD_TYPES = ['number', 'boolean', 'text', 'category'];

type VisibilityRuleComponentProps = {
  conditionalBaseParams: TextCollectionStepParams;
  rule: TextCollectionFieldCondition;
  ruleIndex: number;
  editRule: (ruleIndex: number, rule: TextCollectionFieldCondition) => void;
  deleteRule: (ruleIndex: number) => void;
};

function TextCollectionVisibilityRuleCard({
  onDelete,
  children,
  title,
}: PropsWithChildren<{ title: string; onDelete: () => void }>) {
  return (
    <div className="w-full bg-white border border-neutral-300 shadow-sm rounded-lg py-2 px-4 relative">
      <div>{title}</div>
      <div className="mt-5">{children}</div>
      <div className="absolute top-0 right-0 flex items-center">
        <Icon onClick={onDelete} variant="danger" icon={['fas', 'times']} />
      </div>
    </div>
  );
}

function TextCollectionNumberTypeRule({ editRule, ruleIndex, rule }: VisibilityRuleComponentProps) {
  const setValue = (value: string) => {
    editRule(ruleIndex, {
      ...rule,
      value: Number(value),
    });
  };
  const setOperator = (operator: string) => {
    editRule(ruleIndex, {
      ...rule,
      operator,
    });
  };
  const operators = [
    { id: 'eq', name: 'Equals' },
    { id: 'lt', name: 'Less than' },
    { id: 'lte', name: 'Less than or equals' },
    { id: 'gt', name: 'Greater than' },
    { id: 'gte', name: 'Greater than or equals' },
  ];
  return (
    <div className="flex justify-between items-center">
      <div>
        <span className="font-bold">{rule.field_id}</span>
      </div>
      <div>
        <SelectMenu
          options={operators}
          setSelected={setOperator}
          selectedOptionId={rule.operator}
        />
      </div>
      <div>
        <TextField
          id={`${rule.field_id}-value`}
          type="number"
          value={rule.value}
          onChange={event => setValue(event.target.value)}
        />
      </div>
    </div>
  );
}

function TextCollectionTextTypeRule({ editRule, ruleIndex, rule }: VisibilityRuleComponentProps) {
  const setValue = (value: string) => {
    editRule(ruleIndex, {
      ...rule,
      value,
    });
  };
  const setOperator = (operator: string) => {
    editRule(ruleIndex, {
      ...rule,
      operator,
    });
  };
  const operators = [
    { id: 'eq', name: 'Equals' },
    { id: 'contains', name: 'Contains' },
    { id: 'startsWith', name: 'Starts with' },
    { id: 'endsWith', name: 'Ends with' },
  ];
  return (
    <div className="flex justify-between items-center">
      <div>
        <span className="font-bold">{rule.field_id}</span>
      </div>
      <div>
        <SelectMenu
          options={operators}
          setSelected={setOperator}
          selectedOptionId={rule.operator}
        />
      </div>
      <div>
        <TextField
          id={`${rule.field_id}-value`}
          value={rule.value}
          onChange={event => setValue(event.target.value)}
        />
      </div>
    </div>
  );
}

function TextCollectionBooleanTypeRule({
  editRule,
  rule,
  ruleIndex,
}: VisibilityRuleComponentProps) {
  const setSelected = (value: string) => {
    editRule(ruleIndex, {
      ...rule,
      value: value === 'true',
    });
  };
  return (
    <div className="flex justify-between items-center">
      <div>
        <span className="font-bold">{rule.field_id}</span> must be
      </div>
      <div>
        <SelectMenu<string>
          options={[
            { id: 'true', name: 'True' },
            { id: 'false', name: 'False' },
          ]}
          selectedOptionId={String(rule.value)}
          placeholder="Value"
          setSelected={setSelected}
        />
      </div>
    </div>
  );
}

function TextCollectionCategoryTypeRule({
  editRule,
  rule,
  ruleIndex,
  conditionalBaseParams,
}: VisibilityRuleComponentProps) {
  const setSelected = (value: string) => {
    editRule(ruleIndex, {
      ...rule,
      value,
    });
  };
  const fieldConfig = conditionalBaseParams.params.fields.find(
    field => field.field_id === rule.field_id,
  ) as CategoryField | undefined;
  const choices: { id: string; name: string }[] =
    fieldConfig?.choices.map(choice => ({ id: String(choice.value), name: choice.label })) ?? [];
  return (
    <div className="flex justify-between items-center">
      <div>
        <span className="font-bold">{rule.field_id}</span> must be
      </div>
      <div>
        <SelectMenu
          options={choices}
          selectedOptionId={rule.value || choices?.[0].id}
          setSelected={setSelected}
        />
      </div>
    </div>
  );
}

function TextCollectionVisibilityRule(props: VisibilityRuleComponentProps) {
  const { deleteRule, rule, ruleIndex } = props;
  function DefaultComponent(_props: VisibilityRuleComponentProps) {
    return <p>Not Supported!</p>;
  }
  let RuleCardComponent = DefaultComponent;
  switch (rule.type) {
    case 'number':
      RuleCardComponent = TextCollectionNumberTypeRule;
      break;
    case 'text':
      RuleCardComponent = TextCollectionTextTypeRule;
      break;
    case 'boolean':
      RuleCardComponent = TextCollectionBooleanTypeRule;
      break;
    case 'category':
      RuleCardComponent = TextCollectionCategoryTypeRule;
      break;
    default:
      RuleCardComponent = DefaultComponent;
  }

  return (
    <TextCollectionVisibilityRuleCard
      title={`${props.rule.type} field rule`}
      onDelete={() => deleteRule(ruleIndex)}
    >
      <RuleCardComponent {...props} />
    </TextCollectionVisibilityRuleCard>
  );
}

function getDefaultRuleForType(fieldConfig: any) {
  switch (fieldConfig.type) {
    case 'category': {
      const config = fieldConfig as CategoryField;
      return {
        type: config.type,
        field_id: config.field_id,
        operator: 'eq',
        value: config.choices[0].value,
      };
    }
    case 'boolean': {
      return {
        type: fieldConfig.type,
        field_id: fieldConfig.field_id,
        operator: 'eq',
        value: true,
      };
    }
    default: {
      return {
        type: fieldConfig.type,
        field_id: fieldConfig.field_id,
        operator: 'eq',
        value: '',
      };
    }
  }
}

const CONDITIONAL_SOURCES = ['ModelResponseSelector', 'TextCollection'];

export function TextCollectionBasedConditionalConfig({
  operator,
  onChange,
  previousParams,
}: {
  operator:
    | TextCollectionBasedConditionalVisibility
    | ResponseTextCollectionBasedConditionalVisibility
    | PromptTextCollectionBasedConditionalVisibility;
  onChange(value: any): void;
  previousParams: any[];
}) {
  const conditionalBaseParams = previousParams
    .filter(params => CONDITIONAL_SOURCES.includes(params.type))
    .find(params => params.id === operator.id);

  const fields: [] = conditionalBaseParams?.params.fields.filter((field: any) =>
    SUPPORTED_FIELD_TYPES.includes(field.type),
  );

  const fieldOptions = fields?.map((field: any) => ({
    id: field.field_id,
    name: `${field.field_id}(${field.type}) - ${field.title}`,
  }));

  const conditionalFields = (operator.params?.fields as TextCollectionAndOperator)?.params;

  const addFieldRule = useCallback(
    (field_id: string) => {
      const fieldConfig = fields.find((field: any) => field.field_id === field_id) as any;
      if (!fieldConfig) return;
      onChange(
        produce(operator, draft => {
          draft.params.fields = {
            type: 'AND',
            params: [...conditionalFields, getDefaultRuleForType(fieldConfig)],
          };
        }),
      );
    },
    [operator, onChange, fields, conditionalFields],
  );

  const deleteRule = useCallback(
    (ruleIndex: number) => {
      onChange(
        produce(operator, draft => {
          (draft.params.fields as TextCollectionAndOperator).params.splice(ruleIndex, 1);
        }),
      );
    },
    [operator, onChange],
  );

  const editRule = useCallback(
    (ruleIndex: number, rule: TextCollectionFieldCondition) => {
      onChange(
        produce(operator, draft => {
          const fields = (draft.params.fields as TextCollectionAndOperator).params;
          if (!fields.length) {
            fields.push(rule);
          } else {
            fields[ruleIndex] = rule;
          }
        }),
      );
    },
    [operator, onChange],
  );

  useEffect(() => {
    const baseParams = conditionalBaseParams as TextCollectionStepParams;
    conditionalFields?.forEach((fieldCondition, fieldIndex) => {
      if (
        !baseParams?.params?.fields?.find(
          field => field.field_id === (fieldCondition as TextCollectionFieldCondition).field_id,
        )
      ) {
        deleteRule(fieldIndex);
      }
    });
  }, [conditionalBaseParams, deleteRule, conditionalFields]);

  if (!fieldOptions?.length) {
    return <p>There are no fields to configure conditional visibility for.</p>;
  }

  return (
    <div>
      <SelectMenu
        label="Text Collection Fields"
        placeholder="Add visibility rule for a field"
        options={fieldOptions}
        selectedOptionId={''}
        setSelected={addFieldRule}
      />
      <div className="my-5 flex gap-4 flex-col">
        {conditionalFields?.map((fieldRule, index) => {
          return (
            <div className="flex flex-col items-center" key={index}>
              <TextCollectionVisibilityRule
                conditionalBaseParams={conditionalBaseParams}
                rule={fieldRule as TextCollectionFieldCondition}
                ruleIndex={index}
                editRule={editRule}
                deleteRule={deleteRule}
              />
              <div></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
